const SUBMIT_ENDPOINT = '/bug-reports/submit/'

const GENERIC_ERROR = "That didn't send. Try again in a moment."
const OFFLINE_ERROR = "That didn't send — check your connection and try again."

export const MAX_SCREENSHOTS = 3
export const MAX_SCREENSHOT_BYTES = 1 * 1024 * 1024

/**
 * Send a bug report. The page URL and viewport are captured here so every
 * caller reports the same context. Optional screenshots are sent as multipart.
 *
 * The server answers with { ok, error } and its error strings are written for
 * the reporter, so they are surfaced as-is.
 *
 * @param {Object} report
 * @param {string} report.severity - One of blocker | major | minor | idea
 * @param {string} report.message - What went wrong, in the reporter's words
 * @param {string} [report.reporterEmail] - Follow-up address for anonymous reporters
 * @param {string} [report.website] - Honeypot; real users never fill this in
 * @param {File[]} [report.screenshots] - Optional screenshot files
 * @returns {Promise<{ok: boolean, error?: string}>} Never rejects
 */
export async function submitBugReport({
  severity,
  message,
  reporterEmail = '',
  website = '',
  screenshots = [],
}) {
  const formData = new FormData()
  formData.append('severity', severity)
  formData.append('message', message)
  formData.append('reporter_email', reporterEmail)
  formData.append('website', website)
  formData.append('page_url', window.location.href)
  formData.append('viewport', `${window.innerWidth}x${window.innerHeight}`)
  for (const file of screenshots) {
    formData.append('screenshots', file)
  }

  try {
    // Imported here, not at module scope: AxiosClient imports the store, which
    // imports the user services, which import AxiosClient again. Pulling that
    // cycle in from App.vue's import graph leaves the api client undefined and
    // breaks every service call (login included). Loading it on submit keeps
    // this widget out of the cycle entirely.
    const { default: axiosInstance } = await import('./AxiosClient')
    const { data } = await axiosInstance.post(SUBMIT_ENDPOINT, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return data?.ok ? { ok: true } : { ok: false, error: data?.error || GENERIC_ERROR }
  } catch (err) {
    if (err.response) {
      return { ok: false, error: err.response.data?.error || GENERIC_ERROR }
    }
    return { ok: false, error: OFFLINE_ERROR }
  }
}

export const SEVERITY_OPTIONS = [
  { value: 'blocker', label: "I can't use the app" },
  { value: 'major', label: "Something's broken" },
  { value: 'minor', label: 'Small problem' },
  { value: 'idea', label: 'Suggestion' },
]

export default { submitBugReport, SEVERITY_OPTIONS, MAX_SCREENSHOTS, MAX_SCREENSHOT_BYTES }
